import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut, Hexagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/contexts/CartContext";
import { Cart } from "@/components/Cart";

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, signOut, isAdmin } = useAuth();
  const { totalItems } = useCart();

  const navItems = [
    { name: "Home", href: "/" },
    { name: "Brain Charge", href: "/products/brain-charge" },
    { name: "Our Story", href: "/about" },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <nav className="hive-navigation sticky top-0 z-50 border-b border-accent/20 bg-background/95 text-foreground supports-[backdrop-filter]:bg-background/80 supports-[backdrop-filter]:backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-[64px] items-center justify-between py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-[max(0.5rem,env(safe-area-inset-top))]">
          {/* Logo */}
          <Link to="/" className="group flex min-w-0 items-center gap-3">
            <Hexagon className="h-7 w-7 shrink-0 text-accent" strokeWidth={1.5} aria-hidden="true" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-heading text-base font-bold text-foreground transition-colors group-hover:text-primary sm:text-lg">
                Proton Health Labs
              </span>
              <span className="hidden text-[10px] font-medium uppercase tracking-widest text-primary min-[350px]:block">
                Be a Proton™ • Always Positive
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden flex-1 items-center justify-center gap-1 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                   "min-h-11 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive(item.href) 
                    ? "bg-primary/10 text-primary" 
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden items-center gap-3 md:flex">
            {/* Cart Icon - Badge is built into Cart component */}
            <Cart />
            
            {/* Admin Only - Hidden from regular users */}
            {user && isAdmin && (
              <div className="flex items-center gap-2 pl-2 border-l border-border">
                <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
                  Admin
                </Badge>
                <Button variant="ghost" size="sm" onClick={signOut} className="text-muted-foreground hover:text-foreground">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Cart + Menu button */}
          <div className="flex shrink-0 items-center gap-1 md:hidden sm:gap-2">
            {/* Always visible cart on mobile */}
            <Cart />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 space-y-1 animate-fade-in-up">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex min-h-12 items-center gap-2 px-4 py-3 rounded-md text-base font-medium transition-all",
                  isActive(item.href) 
                    ? "bg-primary/10 text-primary" 
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
              {/* Mobile Admin Only */}
              {user && isAdmin && (
                <div className="space-y-2 pt-2 border-t border-border">
                  <Badge variant="outline" className="w-full justify-center py-1 bg-primary/5 text-primary border-primary/20">
                    Admin Access
                  </Badge>
                  <Button variant="ghost" size="sm" className="w-full" onClick={signOut}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}